// useDraftPersona.ts
import { useState, useEffect, useCallback } from 'react'

const DRAFT_KEY = 'draftPersona'

export interface FileMeta {
  name: string;
  file?: File;       // optional for the draft‐only phase
  path?: string;     // filled in AFTER upload
  uploadedAt?: any;  // filled in AFTER upload
}

export interface DraftPersona {
  fullName: string
  description: string
  links: string[]
  files: FileMeta[]
}

/**
 * Purely local (and localStorage) draft of your persona.
 * No Firebase/Auth side-effects here.
 */
export function useDraftPersona() {
  // 1️⃣ On mount, hydrate from localStorage (merge into full shape)
  const [draft, setDraft] = useState<DraftPersona>(() => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY)
      if (raw) {
        const stored = JSON.parse(raw) as Partial<DraftPersona>
        return {
          fullName: stored.fullName ?? '',
          description: stored.description ?? '',
          links: stored.links ?? [],
          files: [], 
        }
      }
    } catch {}
    return { fullName: '', description: '', links: [], files: [] }
  })

  // 2️⃣ Write back to localStorage on any change
  useEffect(() => {
    localStorage.setItem(DRAFT_KEY, JSON.stringify(draft))
  }, [draft])

  // 3️⃣ (Optional) Clear on hard reload
  useEffect(() => {
    const handler = () => localStorage.removeItem(DRAFT_KEY)
    window.addEventListener('beforeunload', handler)
    return () => window.removeEventListener('beforeunload', handler)
  }, [])

  // 4️⃣ Local helpers
  const addBasicInfo = useCallback((info: { fullName?: string; description?: string }) => {
    setDraft(d => ({ ...d, ...info }))
  }, [])

  const addLink = useCallback((url: string) => {
    setDraft(d => ({ ...d, links: [...d.links, url] }))
  }, [])

  const removeLink = useCallback((url: string) => {
    setDraft(d => ({ ...d, links: d.links.filter(l => l !== url) }))
  }, [])

  const addFile = useCallback((file: File) => {
    setDraft(d => ({
      ...d,
      files: [...d.files, { name: file.name, file }]
    }))
  }, [])

  const removeFile = useCallback((name: string) => {
    setDraft(d => ({
      ...d,
      files: d.files.filter(fm => fm.name !== name)
    }))
  }, [])

  return { draft, addBasicInfo, addLink, removeLink, addFile, removeFile }
}
